/**
 * useStreamRiftDefense - React hook bridging marketStore data to the mm-defense system.
 *
 * Builds a partial TriggerSnapshot from GameRow[] data, evaluates all 11 trigger
 * checks, and returns the computed DefenseStatus. Fill-related and volume fields
 * are stubbed empty until the fill bridge is wired in a future phase.
 */

import { useMemo } from 'react';
import { computeBookMetrics } from '@/lib/pricing/microstructure';
import { evaluateTriggers } from '@/lib/mm-defense/triggers';
import { buildDefenseStatus } from '@/lib/mm-defense/actions';
import type { GameRow } from '@/views/ValueDashboard/types';
import type {
  TriggerSnapshot,
  StrategyTarget,
  MMBookMetrics,
  DefenseStatus,
} from '@/lib/mm-defense/types';

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Extract unique tickers from a GameRow's venue books.
 * Each venue side (away/home) has a marketTicker. We collect all non-empty ones.
 */
function collectTickers(game: GameRow): string[] {
  const tickers: string[] = [];
  if (game.kalshi?.away.marketTicker) tickers.push(game.kalshi.away.marketTicker);
  if (game.kalshi?.home.marketTicker) tickers.push(game.kalshi.home.marketTicker);
  if (game.polymarket?.away.marketTicker) tickers.push(game.polymarket.away.marketTicker);
  if (game.polymarket?.home.marketTicker) tickers.push(game.polymarket.home.marketTicker);
  return tickers;
}

/**
 * Build a StrategyTarget from a game + ticker.
 */
function makeTarget(game: GameRow, ticker: string): StrategyTarget {
  return {
    ticker,
    gameKey: game.key,
    sport: game.sport,
    awayCode: game.awayCode,
    homeCode: game.homeCode,
    enabled: true,
  };
}

/**
 * Convert full BookMetrics to the MMBookMetrics subset used by the defense system.
 */
function toMMBookMetrics(full: ReturnType<typeof computeBookMetrics>): MMBookMetrics {
  return {
    spreadCents: full.spreadCents,
    midPriceCents: full.midPriceCents,
    bestBidCents: full.bestBidCents,
    bestAskCents: full.bestAskCents,
    bidDepthContracts: full.bidDepthContracts,
    askDepthContracts: full.askDepthContracts,
    imbalance: full.imbalance,
  };
}

// ── Hook ─────────────────────────────────────────────────────────

interface UseStreamRiftDefenseResult {
  status: DefenseStatus;
  triggerCount: number;
}

export function useStreamRiftDefense(games: GameRow[]): UseStreamRiftDefenseResult {
  return useMemo(() => {
    const nowMs = Date.now();
    const targets: StrategyTarget[] = [];
    const bookMetrics = new Map<string, MMBookMetrics>();
    const lastUpdateMs = new Map<string, number>();
    const normalSpread = new Map<string, number>();
    const seenTickers = new Set<string>();

    for (const game of games) {
      const tickers = collectTickers(game);

      for (const ticker of tickers) {
        if (seenTickers.has(ticker)) continue;
        seenTickers.add(ticker);

        targets.push(makeTarget(game, ticker));

        // Find the venue book for this ticker to compute book metrics
        const venueBook =
          game.kalshi?.away.marketTicker === ticker ? game.kalshi.away :
          game.kalshi?.home.marketTicker === ticker ? game.kalshi.home :
          game.polymarket?.away.marketTicker === ticker ? game.polymarket.away :
          game.polymarket?.home.marketTicker === ticker ? game.polymarket.home :
          null;

        if (venueBook) {
          const full = computeBookMetrics(venueBook.yes, venueBook.no);
          const mm = toMMBookMetrics(full);
          bookMetrics.set(ticker, mm);

          // Approximate lastUpdate as now (we don't have per-ticker timestamps)
          lastUpdateMs.set(ticker, nowMs);

          // Use current spread as baseline (not ideal but functional without history)
          if (mm.spreadCents !== null && mm.spreadCents > 0) {
            normalSpread.set(ticker, mm.spreadCents);
          }
        }
      }
    }

    const snapshot: TriggerSnapshot = {
      targets,
      bookMetrics,
      lastUpdateMs,
      normalSpread,
      // Stubbed — no fill/volume data wired yet
      unpairedFills: [],
      recentFills: [],
      adverseScores: [],
      volume60s: new Map(),
      volume15mAvg: new Map(),
      correlatedPrices: new Map(),
      exposureByTicker: new Map(),
      nowMs,
    };

    const triggers = evaluateTriggers(snapshot);
    const status = buildDefenseStatus(triggers, undefined, nowMs);
    const triggerCount = triggers.length;

    return { status, triggerCount };
  }, [games]);
}
